import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
    AGGREGATE_NAMES,
    EUROPACE_PROCESS_PLATFORMS,
    LOAN_REQUEST_COMMANDS,
    LOAN_REQUEST_OFFER_SOURCES,
    OFFER_ACCEPTANCE_STATUSES,
} from 'cpool-constants';
import { type LOAN_REQUEST_OFFER_SOURCE_WITHOUT_MANUAL } from 'cpool-constants';
import type { SchemaEuropaceOffer } from 'cpool-schemas';
import type {
    SchemaLoanRequestResolver_Get,
    SchemaLoanRequestResolver_GetOffersAcceptanceStatusByLoanRequestId,
} from 'cpool-tables/backend-resolvers';
import { sleep } from 'cpool-utils';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { getAcceptanceStatusByOfferId } from '../utils/getAcceptanceStatusByOfferId';
import { APICommunicator } from '~/api/APICommunicator';
import useUserInfo from '~/hooks/useUserInfo';
import { queryKeys } from '~/utils/queryKeys';

type Props = {
    currentRequestId?: string;
    lead: SchemaLoanRequestResolver_Get;
    source: LOAN_REQUEST_OFFER_SOURCE_WITHOUT_MANUAL;
    europaceProcessId: string | null | undefined;
};

export const useAcceptOffer = ({ currentRequestId, europaceProcessId, lead, source }: Props) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const user = useUserInfo();
    return useMutation({
        mutationFn: async (offer: SchemaEuropaceOffer) => {
            if (source === LOAN_REQUEST_OFFER_SOURCES.EUROPACE && !europaceProcessId)
                throw new Error('Europace process id is missing');
            let europacePartnerId: string | null;
            switch (lead.europace?.platform) {
                case EUROPACE_PROCESS_PLATFORMS.EUROPACE2:
                    europacePartnerId = user?.europacePlatforms?.europace2.partnerId ?? null;
                    break;
                case EUROPACE_PROCESS_PLATFORMS.STARPOOL:
                    europacePartnerId = user?.europacePlatforms?.starpool.partnerId ?? null;
                    break;
                default:
                    europacePartnerId = null;
            }
            if (!europacePartnerId) {
                throw new Error('Europace partner id is missing');
            }
            return APICommunicator.sendCommand({
                aggregateId: lead.id,
                aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
                commandName: LOAN_REQUEST_COMMANDS.acceptOffer,
                payload: {
                    europaceOffer: offer,
                    id: offer.id,
                    offerId: 'offerId' in offer ? offer.offerId : undefined,
                    offerRequestId: currentRequestId,
                    partnerId: europacePartnerId,
                    processId: europaceProcessId,
                    source,
                    userId: user?.id,
                },
            });
        },
        onError: (_, offer: SchemaEuropaceOffer) => {
            toast.error(t('BANK_CREDIT_REQUEST.OFFERS_TABLE.OFFER_ACCEPTANCE_ERROR'));
            const queryKey = queryKeys.getOfferAcceptanceStatusByOfferId({
                loanRequestId: lead.id,
                offerId: offer.id,
                source,
            });
            queryClient.setQueryData(queryKey, () => {
                return '';
            });
        },
        onMutate: (offer) => {
            const queryKey = queryKeys.getOfferAcceptanceStatusByOfferId({
                loanRequestId: lead.id,
                offerId: offer.id,
                source,
            });
            queryClient.setQueryData(queryKey, () => {
                return OFFER_ACCEPTANCE_STATUSES.PENDING;
            });
            queryClient.setQueryData(
                queryKeys.getOffersAcceptanceStatusByLoanRequestId({
                    loanRequestId: lead.id,
                    source,
                }),
                (data: SchemaLoanRequestResolver_GetOffersAcceptanceStatusByLoanRequestId) => {
                    const existingItem = data.find((item) => item.id === offer.id);
                    if (existingItem) {
                        const newItem = existingItem.acceptanceStatus
                            ? existingItem
                            : { ...existingItem, acceptanceStatus: OFFER_ACCEPTANCE_STATUSES.PENDING };
                        return data.map((item) => (item.id === offer.id ? newItem : item));
                    }
                    return [...data, { acceptanceStatus: OFFER_ACCEPTANCE_STATUSES.PENDING, id: offer.id }];
                },
            );
        },
        onSuccess: async (_, offer) => {
            await sleep(1000);
            getAcceptanceStatusByOfferId({
                loanRequestId: lead.id,
                offerId: offer.id,
                queryClient,
                source,
                t,
            });
        },
    });
};
